import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, fmtGBP, fmtDDMMYYYY, INTERNAL_STAGE_LABELS, APP_TYPE_LABELS, DOC_TYPE_LABELS } from "../../api";
import {
  Users, FileWarning, Receipt, Handshake, Clock, Inbox, ChevronRight, AlertCircle,
} from "lucide-react";

const Card = ({ title, children, subtitle, action }) => (
  <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <header className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
      <div>
        <h3 className="font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="text-[12px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </header>
    {children}
  </section>
);

const Kpi = ({ label, value, hint, icon: Icon, tone = "slate", testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-slate-500">{label}</p>
        <p className="font-serif text-3xl text-slate-800 mt-2">{value}</p>
        {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
      </div>
      <span className={`w-10 h-10 rounded-full bg-${tone}-100 text-${tone}-700 flex items-center justify-center`}>
        <Icon size={17} strokeWidth={1.8} />
      </span>
    </div>
  </div>
);

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setError("");
      const { data } = await api.get(`${API}/dashboard/follow-ups`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load follow-ups");
    }
  };
  useEffect(() => { load(); }, []);

  if (error) return <div data-testid="dash-error" className="bg-rose-50 border border-rose-200 text-rose-700 p-4 rounded"><AlertCircle className="inline mr-2" size={16} />{error}</div>;
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="agency-dashboard">
      <header>
        <p className="text-[11px] tracking-[0.3em] uppercase text-amber-500 font-bold">Agency Back Office</p>
        <h1 className="font-serif text-3xl text-slate-800 mt-1">Today's follow-ups</h1>
        <p className="text-[12px] text-slate-500 mt-1">Every active case needs a next action, an owner and a follow-up date. Below is everything that's open.</p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-unfinished" label="Unfinished forms" value={data.unfinished_forms.length} hint="Customers mid-questionnaire" icon={Inbox} tone="amber" />
        <Kpi testid="kpi-missing-docs" label="Missing docs" value={data.missing_docs.length} hint="Awaiting upload / replacement" icon={FileWarning} tone="rose" />
        <Kpi testid="kpi-pending-handovers" label="Unack handovers" value={data.pending_handovers.length} hint="Awaiting broker acknowledgement" icon={Handshake} tone="sky" />
        <Kpi testid="kpi-overdue-tasks" label="Overdue tasks" value={data.overdue_tasks.length} hint="Past due date" icon={Clock} tone="violet" />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Missing documents" subtitle="Customers see exactly what's outstanding. Separate from broker acceptance.">
          <ul className="divide-y divide-slate-100">
            {data.missing_docs.slice(0, 6).map((d) => (
              <li key={d.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 font-medium truncate">{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</p>
                  <p className="text-[11px] text-slate-500 truncate">{d.related_ref?.label || "—"} · {d.period || "—"}</p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded border border-rose-200 bg-rose-50 text-rose-700">{d.status === "replacement_required" ? "Replace" : "Requested"}</span>
              </li>
            ))}
            {data.missing_docs.length === 0 && <li className="px-5 py-6 text-sm text-slate-500 text-center">All documents collected — nice.</li>}
          </ul>
        </Card>

        <Card title="Unacknowledged handovers" subtitle="Brokers haven't opened these yet.">
          <ul className="divide-y divide-slate-100">
            {data.pending_handovers.slice(0, 6).map((h) => (
              <li key={h.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 font-medium truncate">Case {h.case_id.slice(0, 8)}…</p>
                  <p className="text-[11px] text-slate-500 truncate">Shared {fmtDDMMYYYY(h.created_at)}</p>
                </div>
                <Link to={`/agency/applications/${h.case_id}`} className="text-[11px] font-bold tracking-[0.14em] uppercase text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">Open <ChevronRight size={12} /></Link>
              </li>
            ))}
            {data.pending_handovers.length === 0 && <li className="px-5 py-6 text-sm text-slate-500 text-center">Everything acknowledged.</li>}
          </ul>
        </Card>

        <Card title="Overdue tasks">
          <ul className="divide-y divide-slate-100">
            {data.overdue_tasks.slice(0, 6).map((t) => (
              <li key={t.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 font-medium truncate">{t.title}</p>
                  <p className="text-[11px] text-slate-500">Due {fmtDDMMYYYY(t.due_date)}</p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-700">Open</span>
              </li>
            ))}
            {data.overdue_tasks.length === 0 && <li className="px-5 py-6 text-sm text-slate-500 text-center">No overdue tasks.</li>}
          </ul>
        </Card>

        <Card title="Outstanding referral fees">
          <ul className="divide-y divide-slate-100">
            {data.outstanding_fees.slice(0, 6).map((f) => {
              const bal = Number(f.expected || 0) - Number(f.received_amount || 0);
              return (
                <li key={f.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800 font-medium truncate">Case {f.case_id.slice(0, 8)}…</p>
                    <p className="text-[11px] text-slate-500">Expected {fmtGBP(f.expected)} · Trigger: {f.trigger}</p>
                  </div>
                  <span className="text-[11px] font-bold text-rose-700">{fmtGBP(bal)}</span>
                </li>
              );
            })}
            {data.outstanding_fees.length === 0 && <li className="px-5 py-6 text-sm text-slate-500 text-center">No outstanding fees.</li>}
          </ul>
        </Card>
      </section>
    </div>
  );
}
